import React from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import RoleBasedDashboard from './components/RoleBasedDashboard';
import JobList from './components/JobList';
import AddJob from './components/AddJob';
import InterviewScheduler from './components/InterviewScheduler';
import Kanban from './components/Kanban';
import Insights from './components/Insights';
import ManageUsers from './components/ManageUsers';
import BDsPage from './components/BDsPage';
import Login from './components/Login';
import ProtectedRoute from './components/ProtectedRoute';
import RoleGuard from './components/RoleGuard';
import { JobProvider } from './services/JobContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { PERMISSIONS } from './utils/permissions';
import './components/SkeletonLoader.css';

function Navbar() {
  const location = useLocation();
  const { user, logout, isAuthenticated } = useAuth();
  
  if (!isAuthenticated) return null;
  
  return (
    <nav className="navbar">
      <div className="navbar-content">
        <Link to="/" className="navbar-brand">
          <img src="/assets/logo.png" alt="Dex Logo" style={{ height: '28px', width: '28px' }} />
          <h1>Dex</h1>
        </Link>
        <div className="nav-links">
          <Link to="/" className={`nav-link ${location.pathname === '/' ? 'active' : ''}`}>
            Dashboard
          </Link>
          {user?.role === 'BD' ? (
            <Link to="/bds" className={`nav-link ${location.pathname === '/bds' ? 'active' : ''}`}>
              BDs
            </Link>
          ) : (
            <>
              <Link to="/jobs" className={`nav-link ${location.pathname === '/jobs' ? 'active' : ''}`}>
                Jobs
              </Link>
              <RoleGuard permission={PERMISSIONS.CREATE_JOB} fallback={null}>
                <Link to="/add-job" className={`nav-link ${location.pathname === '/add-job' ? 'active' : ''}`}>
                  Add Job
                </Link>
              </RoleGuard>
            </>
          )}
          <RoleGuard permission={PERMISSIONS.SCHEDULE_INTERVIEW} fallback={null}>
            <Link to="/schedule" className={`nav-link ${location.pathname === '/schedule' ? 'active' : ''}`}>
              Schedule
            </Link>
          </RoleGuard>
          <RoleGuard permission={PERMISSIONS.VIEW_ALL_ANALYTICS} fallback={null}>
            <Link to="/kanban" className={`nav-link ${location.pathname === '/kanban' ? 'active' : ''}`}>
              Kanban
            </Link>
          </RoleGuard>
          <RoleGuard permission={PERMISSIONS.VIEW_ALL_ANALYTICS} fallback={null}>
            <Link to="/insights" className={`nav-link ${location.pathname === '/insights' ? 'active' : ''}`}>
              Insights
            </Link>
          </RoleGuard>
          <RoleGuard permission={PERMISSIONS.MANAGE_USERS} fallback={null}>
            <Link to="/manage-users" className={`nav-link ${location.pathname === '/manage-users' ? 'active' : ''}`}>
              Users
            </Link>
          </RoleGuard>
        </div>
        <div className="nav-user">
          <span className="user-greeting">{user?.name}</span>
          <button className="btn btn-secondary btn-sm" onClick={logout}>
            Log out
          </button>
        </div>
      </div>
    </nav>
  );
}

function AppContent() {
  const { loading } = useAuth();

  if (loading) {
    return (
      <div className="loading-page">
        <div className="skeleton-container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '40px 20px' }}>
          <div className="skeleton skeleton-title" style={{ width: '40%', height: '40px', marginBottom: '30px' }}></div>
          <div className="skeleton skeleton-text" style={{ marginBottom: '15px' }}></div>
          <div className="skeleton skeleton-text" style={{ width: '80%', marginBottom: '15px' }}></div>
          <div className="skeleton skeleton-text" style={{ width: '60%' }}></div>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <Navbar />
      <div className="container">
        <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/" element={
                  <ProtectedRoute>
                    <RoleBasedDashboard />
                  </ProtectedRoute>
                } />
                <Route path="/jobs" element={
                  <ProtectedRoute>
                    <JobList />
                  </ProtectedRoute>
                } />
                <Route path="/add-job" element={
                  <ProtectedRoute>
                    <AddJob />
                  </ProtectedRoute>
                } />
                <Route path="/jobs/edit/:jobId" element={
                  <ProtectedRoute>
                    <AddJob />
                  </ProtectedRoute>
                } />
                <Route path="/schedule" element={
                  <ProtectedRoute>
                    <RoleGuard permission={PERMISSIONS.SCHEDULE_INTERVIEW}>
                      <InterviewScheduler />
                    </RoleGuard>
                  </ProtectedRoute>
                } />
                <Route path="/kanban" element={
                  <ProtectedRoute>
                    <RoleGuard permission={PERMISSIONS.VIEW_ALL_ANALYTICS}>
                      <Kanban />
                    </RoleGuard>
                  </ProtectedRoute>
                } />
                <Route path="/insights" element={
                  <ProtectedRoute>
                    <RoleGuard permission={PERMISSIONS.VIEW_ALL_ANALYTICS}>
                      <Insights />
                    </RoleGuard>
                  </ProtectedRoute>
                } />
                <Route path="/manage-users" element={
                  <ProtectedRoute>
                    <RoleGuard permission={PERMISSIONS.MANAGE_USERS}>
                      <ManageUsers />
                    </RoleGuard>
                  </ProtectedRoute>
                } />
                <Route path="/bds" element={
                  <ProtectedRoute>
                    <BDsPage />
                  </ProtectedRoute>
                } />
        </Routes>
      </div>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <JobProvider>
        <Router>
          <AppContent />
        </Router>
      </JobProvider>
    </AuthProvider>
  );
}

export default App;